import { del, get, post, put } from '@/utils/request'

export interface ImageGenerationRequest {
  query: string
  model: 'DALL_3_STANDARD' | 'DALL_3_HD' | 'DALL_2' | 'GPT_IMAGE_GENERATE' | 'GPT_IMAGE_EDIT'
  number_of_image: number
  image_size: 'square' | 'vertical' | 'horizontal' | 'large' | 'medium' | 'small' | 'square' | 'portrait' | 'landscape'
  image_style?: 'vivid' | 'natural' | 'none' | 'anime' | 'cinematic' | 'photographic' | 'fantasy_art' | 'line_art' | '3d_model' | 'digital_art' | 'origami' | 'comic_book' | 'neonpunk'
  conversation_id?: string
  background_image?: 'auto' | 'opaque' | 'transparent'
  // Always included - uploaded files for GPT Image Edit model, empty array for others
  image_to_edit: Array<{
    type: 'image'
    transfer_method: 'local_file'
    url: string
    upload_file_id: string
  }>
}

export interface ImageGenerationRequestSelfHost {
  prompt: string
  negative: string
  style: string
  pages: number
  width: number
  height: number
  conversation_id?: string
  model: string
}

export interface ImageGenerationRequestNanoBanana {
  prompt: string
  conversation_id?: string
  image_size: '1k' | '2k' | '4k'
  aspect_ratio: '1:1' | '2:3' | '3:2' | '3:4' | '4:3' | '9:16' | '16:9' | '21:9'
  style: string
  model: 'NANO_BANANA' | 'NANO_BANANA_PRO'
  files?: Array<{
    type: 'image'
    transfer_method: 'local_file'
    url: string
    upload_file_id: string
  }>
  tool?: string
}

export interface ImageGenerationResponse {
  success: boolean
  data: {
    id: number | string
    urls: string[]
    query: string
    model: string
    number_of_image: number
    image_size: string
    image_style: string
    conversation_id: string
    generation_time: number
    created_at: string
    files?: ImageFile[]
  }
  error: any
  message: string
  status_code: number
}

export interface ImageHistoryRequest {
  keyword: string
  conversation_id: string
  first_question_of_conversation: boolean
  page?: number
  page_size?: number
}

export interface ImageFile {
  url: string
  agent_generate_image_history_id: number
  id: number
  is_deleted: boolean
}

export interface ImageHistory {
  query: string
  conversation_id: string
  message_id: string
  task_id: string
  dify_id: string
  generation_time: number
  id: number
  files: ImageFile[]
  is_deleted: boolean
  created_by: string
  created_at: string
  updated_by: string | null
  updated_at: string
  name?: string
}

export interface ImageHistoryResponse {
  success: boolean
  data: ImageHistory[]
  error: any
  message: string
  status_code: number
}

export interface ImageHistoryDetailRequest {
  id?: string | number
  source_id?: string | number
  source_type?: string
}

export interface ImageHistoryDetailResponse {
  success: boolean
  data: any
  error: any
  message: string
  status_code: number
}

export interface OptimizePromptResponse {
  success: boolean
  data: {
    prompt: string
  }
}

// Similar image search interfaces
export interface SimilarImageSearchRequest {
  text: string
  top_k?: number
}

export interface SimilarImageResult {
  url: string
  similarity_score: number
  original_query: string
  agent_id: number
}

export interface SimilarImageSearchResponse {
  results: SimilarImageResult[]
  total_found: number
  search_text: string
}

// Generate image
export function generateImage<T = ImageGenerationResponse>(data: ImageGenerationRequest) {
  return post<T>({
    url: '/agents-service/generate-image',
    data,
    timeout: 210000, // 3.5 minutes timeout for streaming
  })
}

// Search image
export function searchImage<T = ImageGenerationResponse>(data: { query: string; page: number; page_size: number }) {
  return post<T>({
    url: '/agents-service/generate-image/search',
    data,
  })
}

// Get image generation history
export function getImageHistory(data: ImageHistoryRequest): Promise<ImageHistoryResponse> {
  return post({
    url: '/agents-service/agent-generate-image-history/get-lst',
    data,
  })
}

export function editConversationName(data: { id: string; name: string;title?: string }): Promise<ImageHistoryResponse> {
  return put({
    url: `/agents-service/agent-generate-image-history/update/${data.id}`,
    data,
  })
}

export function deleteConversationImage(id: string): Promise<ImageHistoryResponse> {
  return del({
    url: `/agents-service/agent-generate-image-history/delete-conversation/${id}`,
  })
}

export function deleteConversationMessage(id: string): Promise<ImageHistoryResponse> {
  return del({
    url: `/agents-service/agent-generate-image-history/delete/${id}`,
  })
}

export function getImageHistoryDetail(data: ImageHistoryDetailRequest): Promise<ImageHistoryDetailResponse> {
  const requestId = data.source_id ?? data.id
  return get({
    url: '/agents-service/agent-generate-image-history/detail-image-generate-history',
    data: {
      id: requestId,
    },
  })
}

export function optimizePromt(data: { query: string; lang: string }): Promise<OptimizePromptResponse> {
  return post({
    url: '/agents-service/generate-prompt/for-image',
    data,
  })
}

// Search similar images
export function searchSimilarImages(data: SimilarImageSearchRequest): Promise<SimilarImageSearchResponse> {
  const requestData = {
    text: data.text,
    top_k: data.top_k ?? 5, // Default value is 5
  }

  return post({
    url: '/agents-service/generate-image/search',
    data: requestData,
  })
}

export function generateImageSelfHost<T = ImageGenerationResponse>(data: ImageGenerationRequestSelfHost) {
  return post<T>({
    url: '/agents-service/generate-image/self-host',
    data,
    timeout: 210000, // 3.5 minutes timeout for streaming
  })
}

export async function generateImageNanoBanana<T = ImageGenerationResponse>(data: ImageGenerationRequestNanoBanana) {
  const formData = new FormData()

  // Add files if any
  if (data.files && data.files.length > 0) {
    // Fetch and append each file to FormData
    for (const file of data.files) {
      try {
        const response = await fetch(file.url)
        const blob = await response.blob()
        formData.append('files', blob, file.upload_file_id)
      }
      catch (error) {
        console.error('Error fetching file:', error)
      }
    }
  }

  // Add data as JSON string
  const dataPayload = {
    prompt: data.prompt,
    conversation_id: data.conversation_id || '',
    image_size: data.image_size,
    aspect_ratio: data.aspect_ratio,
    style: data.style,
    model: data.model,
    tool: data.tool || '',
  }

  formData.append('data', JSON.stringify(dataPayload))

  return post<T>({
    url: '/agents-service/generate-image/genai-image',
    data: formData,
    timeout: 150000, // 2.5 minutes timeout for streaming
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}
